import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Person, PersonFields, PersonState } from './types';
import {
	getPerson,
	getPersons,
	getPersonsBookList,
	updatePerson,
  createPerson
} from './personThunk';
import { CustomError } from 'shared/api';

const initialState: PersonState = {
	persons: [],
	personBookList: [],
	selectedPerson: null,
	selectedPersonId: 0,
	defaultPerson: null,
	loading: false,
	isLoadingPerson: false,
	error: null,
	errorPerson: null,
};

const personSlice = createSlice({
	name: 'person',
	initialState,
	reducers: {
		resetPersonDetail: state => {
			state.selectedPerson = null;
			state.selectedPersonId = 0;
		},
		setSelectedPerson: (state, action: PayloadAction<Person>) => {
			state.selectedPerson = action.payload;
		},
		resetError: state => {
			state.error = null;
		},
		updatePersonBookItem: (
			state,
			action: PayloadAction<{
				id: number;
				updates: PersonFields;
			}>,
		) => {
			const { id, updates } = action.payload;
			const item = state.personBookList.find(item => item.id === id);
			if (item) {
				Object.entries(updates).map(([key, value]) => {
					item[key] = value;
				});
			}
		},
	},
	extraReducers: builder => {
		builder
			.addCase(getPersons.pending, state => {
				state.loading = true;
				state.error = null;
			})
			.addCase(getPersons.fulfilled, (state, action) => {
				const persons = action.payload.results;
				const page = action.meta.arg.page;
				if (page && page > 1) {
					state.persons = state.persons.concat(persons);
				} else {
					state.persons = persons;
				}

				state.loading = false;
			})
			.addCase(getPersons.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload as CustomError;
			})

			.addCase(getPersonsBookList.pending, state => {
				state.loading = true;
				state.error = null;
			})
			.addCase(getPersonsBookList.fulfilled, (state, action) => {
				const persons = action.payload.results;
				const page = action.meta.arg.page;
				if (page && page > 1) {
					state.personBookList = state.personBookList.concat(persons);
				} else {
					state.personBookList = persons;
				}

				state.loading = false;
			})
			.addCase(getPersonsBookList.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload as CustomError;
			})

			.addCase(getPerson.pending, (state, action) => {
				state.errorPerson = null;
				state.isLoadingPerson = true;
				state.selectedPersonId = action.meta.arg.id;
			})
			.addCase(getPerson.fulfilled, (state, action) => {
				state.isLoadingPerson = false;
				const isDefaultPerson = action.meta.arg.isDefaultPerson;
				const person = action.payload;
				if (isDefaultPerson) {
					state.defaultPerson = person;
				} else {
					state.selectedPerson = person;
				}
			})
			.addCase(getPerson.rejected, (state, action) => {
				state.isLoadingPerson = false;
				state.errorPerson = action.payload as CustomError;
			})

			.addCase(updatePerson.pending, state => {
				state.error = null;
				state.loading = true;
			})
			.addCase(updatePerson.fulfilled, (state, action) => {
				const updatedPerson = action.payload;
				state.selectedPerson = updatedPerson;
				state.persons.forEach((person, key) => {
					if (person.id === updatedPerson.id) {
						state.persons[key] = updatedPerson;
					}
				});
				state.loading = false;
			})
			.addCase(updatePerson.rejected, (state, action) => {
				state.error = action.payload as CustomError;
				state.loading = false;
			})

			.addCase(createPerson.pending, state => {
				state.error = null;
				state.loading = true;
			})
			.addCase(createPerson.fulfilled, (state, action) => {
        state.persons.unshift(action.payload);
				state.selectedPerson = action.payload;
				state.loading = false;
			})
			.addCase(createPerson.rejected, (state, action) => {
				state.error = action.payload as CustomError;
				state.loading = false;
			});
	},
});

export const personReducer = personSlice.reducer;
export const {
	resetPersonDetail,
	setSelectedPerson,
	resetError,
	updatePersonBookItem,
} = personSlice.actions;
